import { Component, OnInit, OnChanges, SimpleChanges, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from '@openng/optimus-ui/table';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TagModule } from '@openng/optimus-ui/tag';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { SelectModule } from '@openng/optimus-ui/select';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ToastModule } from '@openng/optimus-ui/toast';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { MessageService } from '@openng/optimus-ui/api';

import { ReleaseDownload, ReleaseDownloadFilter } from '../../../shared/models/release.model';
import { ReleaseService } from '../../../core/services/release.service';

@Component({
  selector: 'app-releases-downloads',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    DatePickerModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ToastModule,
    TooltipModule
  ],
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './releases-downloads.component.html'
})
export class ReleasesDownloadsComponent implements OnInit, OnChanges {
  @Input() versionFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All Versions', value: null }
  ];
  @Input() initialVersion: string | null = null;

  private releaseService = inject(ReleaseService);
  private messageService = inject(MessageService);

  downloads: ReleaseDownload[] = [];
  downloadsLoading = false;
  totalDownloads = 0;
  downloadSearch = '';
  selectedVersionFilter: string | null = null;
  selectedSourceFilter: string | null = null;
  downloadRangeDates: Date[] | null = null;
  downloadPage = 1;
  downloadPageSize = 50;

  readonly sourceFilterOptions = [
    { label: 'All Sources', value: null },
    { label: 'CMS Portal', value: 'CMS' },
    { label: 'Desktop Agent', value: 'DesktopAgent' }
  ];

  ngOnInit(): void {
    if (this.initialVersion) {
      this.selectedVersionFilter = this.initialVersion;
    }
    this.loadDownloads();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialVersion'] && !changes['initialVersion'].firstChange) {
      this.selectedVersionFilter = this.initialVersion;
      this.downloadPage = 1;
      this.loadDownloads();
    }
  }

  loadDownloads(): void {
    this.downloadsLoading = true;

    const filter: ReleaseDownloadFilter = {
      page: this.downloadPage,
      pageSize: this.downloadPageSize,
      search: this.downloadSearch.trim() || undefined,
      version: this.selectedVersionFilter || undefined,
      source: this.selectedSourceFilter || undefined
    };

    if (this.downloadRangeDates && this.downloadRangeDates[0]) {
      filter.startDate = this.downloadRangeDates[0];
      if (this.downloadRangeDates[1]) {
        const end = new Date(this.downloadRangeDates[1]);
        end.setHours(23, 59, 59, 999);
        filter.endDate = end;
      }
    }

    this.releaseService.getDownloads(filter).subscribe({
      next: (page) => {
        this.downloads = page?.items ?? [];
        this.totalDownloads = page?.totalCount ?? 0;
        this.downloadsLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load download history'
        });
        this.downloads = [];
        this.totalDownloads = 0;
        this.downloadsLoading = false;
      }
    });
  }

  onDownloadsPage(event: any): void {
    this.downloadPage = Math.floor(event.first / (event.rows || this.downloadPageSize)) + 1;
    this.downloadPageSize = event.rows || this.downloadPageSize;
    this.loadDownloads();
  }

  onDownloadFilterChange(): void {
    this.downloadPage = 1;
    this.loadDownloads();
  }

  clearDownloadFilters(): void {
    this.downloadSearch = '';
    this.selectedVersionFilter = null;
    this.selectedSourceFilter = null;
    this.downloadRangeDates = null;
    this.downloadPage = 1;
    this.loadDownloads();
  }

  exportDownloadsCsv(): void {
    const filter: ReleaseDownloadFilter = {
      search: this.downloadSearch.trim() || undefined,
      version: this.selectedVersionFilter || undefined,
      source: this.selectedSourceFilter || undefined
    };

    if (this.downloadRangeDates && this.downloadRangeDates[0]) {
      filter.startDate = this.downloadRangeDates[0];
      if (this.downloadRangeDates[1]) {
        const end = new Date(this.downloadRangeDates[1]);
        end.setHours(23, 59, 59, 999);
        filter.endDate = end;
      }
    }

    this.releaseService.exportDownloadsCsv(filter).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `anguleris-plugin-downloads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to export download history'
        });
      }
    });
  }

  getDownloadSourceSeverity(source?: string): 'info' | 'success' | 'secondary' {
    if (!source) return 'secondary';
    const s = source.toLowerCase();
    if (s.includes('agent')) return 'success';
    if (s.includes('cms')) return 'info';
    return 'secondary';
  }

  getDownloadSourceLabel(source?: string): string {
    if (!source) return 'Direct / Unknown';
    if (source.toLowerCase() === 'desktopagent' || source.toLowerCase() === 'agent') {
      return 'Desktop Agent';
    }
    if (source.toUpperCase() === 'CMS') {
      return 'CMS Portal';
    }
    return source;
  }

  formatUser(d: ReleaseDownload): string {
    if (d.userName && d.userEmail) {
      return `${d.userName} (${d.userEmail})`;
    }
    if (d.userEmail) return d.userEmail;
    if (d.userName) return d.userName;
    return '—';
  }

  formatComputer(d: ReleaseDownload): string {
    if (d.computerName && d.windowsUserName) {
      return `${d.computerName} \\ ${d.windowsUserName}`;
    }
    if (d.computerName) return d.computerName;
    if (d.windowsUserName) return d.windowsUserName;
    return '—';
  }

  formatInstalledVersion(d: ReleaseDownload): string {
    if (!d.installedVersion) return 'New Install';
    return `v${d.installedVersion}`;
  }
}
